import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { selectFreePlan } from "@/lib/payments.functions";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import {
  Building2, MapPin, Users, Check, Sparkles, Upload, Plus, Trash2,
  Navigation, Siren, Clock, CreditCard, Send,
} from "lucide-react";
import { trackFunnel } from "@/lib/funnel";
import { createInvite, resendInvite, revokeInvite } from "@/lib/invites.functions";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
  validateSearch: (s: Record<string, unknown>): { paid?: true } => ({
    paid: s.paid === "1" || s.paid === 1 ? true : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Set up your workspace — MissionReady" },
      { name: "description", content: "Create your MissionReady workspace: add your first site, invite officers, choose modules, and pick a plan in a guided 5-step setup." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Set up your workspace — MissionReady" },
      { property: "og:description", content: "Guided 5-step setup for your MissionReady workspace: organization, first site, team invites, modules, and plan." },
      { property: "og:url", content: "https://missionready.dev/onboarding" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/onboarding" }],
  }),
});

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}

type Step = 1 | 2 | 3 | 4 | 5;
type Role = "admin" | "supervisor" | "dispatcher" | "officer";
type InviteDraft = { email: string; role: Role };
type Flags = { gps_tracking: boolean; sos_enabled: boolean; time_clock_enabled: boolean };
type Plan = {
  id: string; key: string; name: string; description: string | null;
  price_cents: number; currency: string; interval: string;
  features: string[]; stripe_price_id: string | null; sort_order: number;
};

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "supervisor", label: "Supervisor" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "officer", label: "Officer" },
];

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: "Workspace" },
  { n: 2, label: "First site" },
  { n: 3, label: "Invite team" },
  { n: 4, label: "Modules" },
  { n: 5, label: "Plan" },
];

function OnboardingPage() {
  const { user, loading, membershipsLoading, memberships, refreshMemberships, setCurrentOrgId } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(true);
  const [autoSkipping, setAutoSkipping] = useState(false);
  const [accountType, setAccountType] = useState<"agency" | "solo">("agency");
  const isSolo = accountType === "solo";
  // Solo Contractor (paid) keeps the solo flow but offers its monthly/yearly subscription.
  const [soloPaid, setSoloPaid] = useState(false);
  const isSoloFree = isSolo && !soloPaid;


  // Step 1
  const [orgName, setOrgName] = useState("");
  const [orgId, setOrgId] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Step 2
  const [siteName, setSiteName] = useState("");
  const [siteAddress, setSiteAddress] = useState("");

  // Step 3
  const [invites, setInvites] = useState<InviteDraft[]>([{ email: "", role: "officer" }]);
  const [pendingInvites, setPendingInvites] = useState<Array<{
    id: string; email: string; role: string; token: string;
    accepted_at: string | null; expires_at: string | null;
  }>>([]);
  const [pendingBusyId, setPendingBusyId] = useState<string | null>(null);

  // Step 4
  const [flags, setFlags] = useState<Flags>({
    gps_tracking: true,
    sos_enabled: true,
    time_clock_enabled: true,
  });

  // Step 5 — plans
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("month");
  const [chosenPlanKey, setChosenPlanKey] = useState<string | null>(null);
  const chooseFree = useServerFn(selectFreePlan);
  const createInviteFn = useServerFn(createInvite);
  const resendInviteFn = useServerFn(resendInvite);
  const revokeInviteFn = useServerFn(revokeInvite);

  const { openCheckout, closeCheckout, isOpen: checkoutOpen, checkoutElement } = useStripeCheckout();

  // Resume: if user already has an org but isn't done, jump to saved step.
  useEffect(() => {
    if (loading || membershipsLoading) return;
    if (!user) { navigate({ to: "/signup", replace: true }); return; }

    // Plan-driven branching: the selected plan is the source of truth.
    // Free + Solo plans → solo flow (skip site/invites, business name optional).
    // Starter / team plans → full agency flow (org name + site + invites required).
    let planKey: string | null = null;
    try { planKey = localStorage.getItem("mr_plan_preselect"); } catch { /* ignore */ }
    let solo = planKey === "free" || planKey === "solo";
    // Fallback signals when plan wasn't preselected (e.g. direct /onboarding visit).
    if (!planKey) {
      try { solo = localStorage.getItem("mr_account_type") === "solo"; } catch { /* ignore */ }
      const metaType = (user.user_metadata as { account_type?: string } | undefined)?.account_type;
      if (metaType === "solo") solo = true;
    }
    const at: "agency" | "solo" = solo ? "solo" : "agency";
    setAccountType(at);
    setSoloPaid(planKey === "solo");


    (async () => {
      if (memberships.length === 0) {
        // Prefill solo workspace name
        if (at === "solo") {
          const fullName =
            (user.user_metadata as { full_name?: string } | undefined)?.full_name?.trim() ||
            (user.email?.split("@")[0] ?? "My");
          setOrgName(`${fullName}'s Workspace`);
        }
        setResuming(false);
        return;
      }
      const m = memberships[0];
      const { data } = await supabase
        .from("organizations")
        .select("id, name, logo_url, onboarding_completed, onboarding_step")
        .eq("id", m.organization_id)
        .maybeSingle();
      if (!data) { setResuming(false); return; }
      if ((data as any).onboarding_completed) {
        setCurrentOrgId(m.organization_id);
        navigate({ to: "/home", replace: true });
        return;
      }
      setOrgId(data.id);
      setOrgName(data.name ?? "");
      if (data.logo_url) setLogoPreview(data.logo_url);
      const saved = Math.min(5, Math.max(1, (data as any).onboarding_step ?? 1)) as Step;
      setStep(saved);
      setResuming(false);
    })();
  }, [loading, membershipsLoading, user, memberships, navigate, setCurrentOrgId]);


  const persistStep = async (next: Step) => {
    if (!orgId) return;
    await supabase
      .from("organizations")
      .update({ onboarding_step: next } as never)
      .eq("id", orgId);
  };

  const onPickLogo = (file: File | null) => {
    setLogoFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
    }
  };

  const uploadLogo = async (newOrgId: string): Promise<string | null> => {
    if (!logoFile) return null;
    const ext = logoFile.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${newOrgId}/logo-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("org-branding")
      .upload(path, logoFile, { upsert: true, contentType: logoFile.type });
    if (upErr) {
      toast.error(`Logo upload failed: ${upErr.message}`);
      return null;
    }
    const { data: signed, error: sErr } = await supabase.storage
      .from("org-branding")
      .createSignedUrl(path, 60 * 60 * 24 * 365);
    if (sErr || !signed?.signedUrl) return null;
    return signed.signedUrl;
  };

  // Step 1 — create org + upload logo
  const submitOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !orgName.trim()) return;
    setBusy(true);
    try {
      let activeOrgId = orgId;
      if (!activeOrgId) {
        const slug = `${slugify(orgName)}-${Math.random().toString(36).slice(2, 6)}`;
        const { data, error } = await supabase.rpc("create_organization_with_trial", {
          _name: orgName.trim(), _slug: slug,
        });
        if (error || !data) throw new Error(error?.message ?? "Failed to create workspace");
        activeOrgId = data as unknown as string;
        setOrgId(activeOrgId);
        await refreshMemberships();
        setCurrentOrgId(activeOrgId);
      } else {
        await supabase.from("organizations").update({ name: orgName.trim() } as never).eq("id", activeOrgId);
      }

      let logoUrl: string | null = null;
      if (logoFile) {
        logoUrl = await uploadLogo(activeOrgId!);
        if (logoUrl) {
          await supabase.from("organizations").update({ logo_url: logoUrl } as never).eq("id", activeOrgId!);
          await supabase.from("org_branding").upsert(
            { organization_id: activeOrgId!, logo_url: logoUrl } as never,
            { onConflict: "organization_id" },
          );
        }
      }

      if (isSolo) {
        // Solo path: save default modules, skip site + invites, jump to Free plan picker.
        await supabase
          .from("org_feature_flags")
          .upsert({ organization_id: activeOrgId!, ...flags } as never, { onConflict: "organization_id" });
        await persistStep(5);
        setStep(5);
      } else {
        await persistStep(2);
        setStep(2);
      }

    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast.error(/not authenticated/i.test(msg) ? "Your session expired. Please sign in again." : msg);
    } finally {
      setBusy(false);
    }
  };

  // Step 2 — first site
  const submitSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !siteName.trim()) return;
    setBusy(true);
    const { error } = await supabase.from("sites").insert({
      organization_id: orgId,
      name: siteName.trim(),
      address: siteAddress.trim() || null,
    } as never);
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Site added");
    await persistStep(3);
    setStep(3);
  };

  // Step 3 — load any pending invites so users can resend if email failed
  const loadPendingInvites = async () => {
    if (!orgId) return;
    const { data } = await supabase
      .from("organization_invites")
      .select("id,email,role,token,accepted_at,expires_at")
      .eq("organization_id", orgId)
      .is("accepted_at", null)
      .order("created_at", { ascending: false });
    setPendingInvites((data ?? []) as typeof pendingInvites);
  };

  useEffect(() => {
    if (step === 3 && orgId) loadPendingInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, orgId]);

  // Step 3 — invites with role selector
  const updateInvite = (i: number, patch: Partial<InviteDraft>) =>
    setInvites(prev => prev.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const addInvite = () => setInvites(prev => [...prev, { email: "", role: "officer" }]);
  const removeInvite = (i: number) =>
    setInvites(prev => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const submitInvites = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !user) return;
    const clean = invites
      .map(v => ({ email: v.email.trim().toLowerCase(), role: v.role }))
      .filter(v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email));
    if (clean.length === 0) {
      toast.error("Add at least one teammate to invite.");
      return;
    }
    setBusy(true);
    const origin = typeof window !== "undefined" ? window.location.origin : undefined;
    let emailed = 0;
    let failed = 0;
    for (const v of clean) {
      try {
        const res: any = await createInviteFn({
          data: { orgId, email: v.email, role: v.role as any, origin },
        });
        if (res?.emailSent) emailed += 1; else failed += 1;
      } catch (err: any) {
        failed += 1;
        toast.error(err?.message ?? `Couldn't invite ${v.email}`);
      }
    }
    setBusy(false);
    if (emailed > 0) toast.success(`${emailed} invite${emailed === 1 ? "" : "s"} emailed`);
    if (failed > 0) toast.warning(`${failed} invite${failed === 1 ? "" : "s"} created but not emailed — copy the link or resend below or from Team.`);
    await loadPendingInvites();
    await persistStep(4);

    setStep(4);
  };

  const resendPending = async (inv: { id: string; token: string; email: string }) => {
    setPendingBusyId(inv.id);
    try {
      const updated: any = await resendInviteFn({
        data: { inviteId: inv.id, origin: typeof window !== "undefined" ? window.location.origin : undefined },
      });
      const token = updated?.token ?? inv.token;
      if (typeof window !== "undefined") {
        const link = `${window.location.origin}/accept-invite/${token}`;
        try { await navigator.clipboard.writeText(link); } catch { /* clipboard optional */ }
      }
      if (updated?.emailSent) toast.success(`Invite re-sent to ${updated.email} — link also copied`);
      else toast.warning(`Invite extended, but the email couldn't be sent${updated?.emailError ? ` (${updated.emailError})` : ""}. Link copied — share it directly.`);
      await loadPendingInvites();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't resend invite");
    } finally { setPendingBusyId(null); }
  };

  const revokePending = async (id: string) => {
    if (typeof window !== "undefined" && !window.confirm("Revoke this invitation?")) return;
    setPendingBusyId(id);
    try {
      await revokeInviteFn({ data: { inviteId: id } });
      toast.success("Invite revoked — seat freed");
      await loadPendingInvites();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't revoke invite");
    } finally { setPendingBusyId(null); }
  };

  // Step 4 — save feature flags then move to plan picker
  const saveModulesAndContinue = async () => {
    if (!orgId) return;
    setBusy(true);
    const { error: fErr } = await supabase
      .from("org_feature_flags")
      .upsert({ organization_id: orgId, ...flags } as never, { onConflict: "organization_id" });
    if (fErr) console.warn("Flags upsert:", fErr.message);
    await persistStep(5);
    setBusy(false);
    setStep(5);
  };

  // Step 5 — load active plans
  useEffect(() => {
    if (step !== 5) return;
    try {
      const iv = localStorage.getItem("mr_interval_preselect");
      const tier = localStorage.getItem("mr_tier_preselect");
      if (iv === "year" || iv === "month") setBillingInterval(iv);
      if (tier) setChosenPlanKey(iv === "year" ? `${tier}_annual` : tier);
    } catch { /* ignore */ }
    (async () => {
      const { data } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("active", true)
        .order("sort_order");
      setPlans(((data ?? []) as unknown) as Plan[]);
    })();
  }, [step]);

  // Step 5 — final completion (called after plan choice OR skip OR successful checkout return)
  // For solo plans we pause on a success screen to surface the "invite a backup / fill your seats"
  // CTA before sending the user to the dashboard.
  const [soloDone, setSoloDone] = useState(false);
  const finish = async (opts: { skipSoloSuccess?: boolean } = {}) => {
    if (!orgId) return;
    setBusy(true);
    const { error } = await supabase
      .from("organizations")
      .update({ onboarding_completed: true, onboarding_step: 5 } as never)
      .eq("id", orgId);
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    try { localStorage.setItem(`mr_just_onboarded:${orgId}`, "1"); } catch { /* ignore */ }
    await refreshMemberships();
    setCurrentOrgId(orgId);
    if (isSolo && !opts.skipSoloSuccess) {
      setSoloDone(true);
      return;
    }
    navigate({ to: "/home", replace: true });
  };


  const pickFree = async () => {
    if (!orgId) return;
    setBusy(true);
    try {
      await chooseFree({ data: { organizationId: orgId, environment: getStripeEnvironment() } });
      toast.success("You're on the Free Forever plan.");
      await finish();
    } catch (e) {
      setBusy(false);
      toast.error(e instanceof Error ? e.message : "Could not activate the Free plan");
    }
  };

  const pickPaid = (plan: Plan) => {
    if (!orgId || !user) return;
    if (!plan.stripe_price_id) { toast.error("This plan isn't connected to payments yet."); return; }
    if (!isPaymentsConfigured()) { toast.error("Payments aren't configured in this environment."); return; }
    setChosenPlanKey(plan.key);
    openCheckout({
      priceId: plan.stripe_price_id,
      organizationId: orgId,
      organizationName: orgName,
      customerEmail: user.email ?? undefined,
      returnUrl: `${window.location.origin}/onboarding?paid=1`,
    });
  };

  // If Stripe sent the user back with paid=1, auto-complete onboarding.
  useEffect(() => {
    if (!search.paid || !orgId || resuming) return;
    toast.success("Subscription activated 🎉");
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.paid, orgId, resuming]);

  // Honor the plan the user chose on /signup so we don't make them re-pick.
  // Only auto-activates the Free path here — paid plans still surface the
  // 30-day-trial checkout button so the user explicitly confirms billing.
  const autoFreeFired = useRef(false);
  useEffect(() => {
    if (step !== 5 || !orgId || busy || autoFreeFired.current) return;
    let preselect: string | null = null;
    try { preselect = localStorage.getItem("mr_plan_preselect"); } catch { /* ignore */ }
    if (preselect === "free") {
      autoFreeFired.current = true;
      pickFree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, orgId, busy]);

  // Google-signup fast lane: a user who came in via Google AND chose a solo
  // path (Individual or Solo Contractor) needs ZERO onboarding clicks — we
  // know their name from Google, the workspace name defaults to
  // "{name}'s Workspace", and the plan is preselected. Create the workspace
  // inline and let the plan auto-activator above finish the job.
  const autoCreateFired = useRef(false);
  useEffect(() => {
    if (resuming || loading || membershipsLoading || busy) return;
    if (!user || autoCreateFired.current) return;
    if (memberships.length > 0 || orgId) return;
    if (!isSolo) return; // agency path still needs the explicit form

    // Only auto-create for users who arrived via Google — email/password
    // signups land here with no name validation done yet, so let the
    // explicit form run for them.
    const identities = (user.identities ?? []) as { provider?: string }[];
    const fromGoogle = identities.some((i) => i.provider === "google");
    if (!fromGoogle) return;

    autoCreateFired.current = true;
    const meta = (user.user_metadata ?? {}) as { full_name?: string; name?: string };
    const fullName =
      meta.full_name?.trim() || meta.name?.trim() || user.email?.split("@")[0] || "My";
    const defaultName = `${fullName}'s Workspace`;
    setOrgName(defaultName);
    setAutoSkipping(true);
    trackFunnel("google_signup_autoskipped", { account_type: "solo" });

    (async () => {
      setBusy(true);
      try {
        const slug = `${slugify(defaultName)}-${Math.random().toString(36).slice(2, 6)}`;
        const { data, error } = await supabase.rpc("create_organization_with_trial", {
          _name: defaultName, _slug: slug,
        });
        if (error || !data) throw new Error(error?.message ?? "Failed to create workspace");
        const newOrgId = data as unknown as string;
        setOrgId(newOrgId);
        await refreshMemberships();
        setCurrentOrgId(newOrgId);
        await supabase
          .from("org_feature_flags")
          .upsert({ organization_id: newOrgId, ...flags } as never, { onConflict: "organization_id" });
        await persistStep(5);
        setStep(5);
        // autoFreeFired effect above will activate the Free plan and call finish().
      } catch (err) {
        autoCreateFired.current = false; // allow manual retry via the visible form
        setAutoSkipping(false);
        const msg = err instanceof Error ? err.message : "Something went wrong";
        toast.error(msg);
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resuming, loading, membershipsLoading, busy, user, memberships.length, orgId, isSolo]);


  if (loading || resuming || autoSkipping) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-hero">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-hero px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex justify-center"><Logo /></div>

        <ol className="mb-6 flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground">
          {(isSolo
            ? [
                { n: 1 as Step, label: "Workspace" },
                { n: 5 as Step, label: "Plan" },
                { n: 6 as Step, label: "Invite" },
              ]
            : STEPS
          ).map((s, i, arr) => {
            const effective = soloDone ? 6 : step;
            const reached = effective >= s.n;
            const passed = effective > s.n;
            return (
              <li key={s.n} className="flex items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                  reached ? "border-primary bg-primary text-primary-foreground" : "border-border"
                }`}>
                  {passed ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className={reached ? "text-foreground" : ""}>{s.label}</span>
                {i < arr.length - 1 && <span className="mx-1 h-px w-6 bg-border" />}
              </li>
            );
          })}
        </ol>



        <div
          className="rounded-2xl border border-border bg-card p-8 shadow-elegant"
          style={{ background: "linear-gradient(180deg, var(--card), color-mix(in oklab, var(--card), var(--primary) 4%))" }}
        >
          {step === 1 && (
            <form onSubmit={submitOrg} className="space-y-5">
              <Header
                icon={Building2}
                title={isSolo ? "Name your workspace" : "Create your workspace"}
                subtitle={isSolo
                  ? "You don't need a business or organization to use MissionReady. This is just a label for your personal workspace — keep what we filled in, rename it, or use your own name."
                  : "Team plans run under a company workspace. Add your company / agency name and (optionally) a logo — you'll add your first site and invite teammates next."}
              />
              <div>
                <Label htmlFor="orgName">
                  {isSolo ? (
                    <>Workspace name <span className="font-normal text-muted-foreground">(business name optional)</span></>
                  ) : (
                    "Company / agency name"
                  )}
                </Label>
                <Input
                  id="orgName" required value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={isSolo ? "e.g. Jane Doe (or your business name)" : "e.g. Northwind Patrol Services"}
                  className="mt-1"
                />
                {isSolo && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Solo contractor or individual? Just use your name — no business required.
                  </p>
                )}
              </div>

              <div>
                <Label>Logo</Label>
                <div className="mt-1 flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/40">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="h-full w-full object-cover" />
                    ) : (
                      <Building2 className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => onPickLogo(e.target.files?.[0] ?? null)}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                      <Upload className="mr-2 h-4 w-4" />
                      {logoPreview ? "Replace logo" : "Upload logo"}
                    </Button>
                    <p className="mt-1 text-xs text-muted-foreground">PNG, JPG, SVG or WebP up to ~2&nbsp;MB. Optional.</p>
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={busy || !orgName.trim()}>
                {busy ? "Saving…" : "Continue"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitSite} className="space-y-5">
              <Header
                icon={MapPin}
                title="Add your first site"
                subtitle="The location your team patrols or staffs. You can add more later."
              />
              <div>
                <Label htmlFor="siteName">Site name</Label>
                <Input
                  id="siteName" required value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="e.g. Downtown Plaza"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="siteAddr">Address</Label>
                <Input
                  id="siteAddr" value={siteAddress}
                  onChange={(e) => setSiteAddress(e.target.value)}
                  placeholder="123 Main St, City, ST"
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>Back</Button>
                <Button type="submit" className="flex-1" disabled={busy || !siteName.trim()}>
                  {busy ? "Saving…" : "Continue"}
                </Button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={submitInvites} className="space-y-5">
              <Header
                icon={Users}
                title="Invite your team"
                subtitle="Invite at least one teammate. Pick a role per person — you can change it later."
              />

              {pendingInvites.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Pending invitations ({pendingInvites.length})</h3>
                    <span className="text-[11px] text-muted-foreground">Resend if the email wasn’t received</span>
                  </div>
                  <ul className="divide-y divide-border">
                    {pendingInvites.map((inv) => {
                      const expires = inv.expires_at ? new Date(inv.expires_at) : null;
                      const expired = expires ? expires.getTime() < Date.now() : false;
                      const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / 86_400_000) : null;
                      return (
                        <li key={inv.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{inv.email}</div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                              <span className="rounded-full border border-border bg-secondary px-2 py-0.5">{inv.role}</span>
                              {expired ? (
                                <span className="text-destructive">Expired</span>
                              ) : (
                                <span>{daysLeft != null && daysLeft > 0 ? `${daysLeft}d left` : "expires today"}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button" size="sm" variant="ghost"
                              disabled={pendingBusyId === inv.id}
                              onClick={() => resendPending(inv)}
                              className="gap-1"
                            >
                              <Send className="h-4 w-4" />
                              {expired ? "Renew" : "Resend"}
                            </Button>
                            <Button
                              type="button" size="sm" variant="ghost"
                              className="text-destructive"
                              disabled={pendingBusyId === inv.id}
                              onClick={() => revokePending(inv.id)}
                            >
                              Revoke
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="space-y-2">
                {invites.map((v, i) => (
                  <div key={i} className="grid gap-2 sm:grid-cols-[1fr_180px_auto]">
                    <Input
                      type="email"
                      autoComplete="off"
                      aria-label={`Teammate ${i + 1} email address`}
                      placeholder="teammate@company.com"
                      value={v.email}
                      onChange={(e) => updateInvite(i, { email: e.target.value })}
                      required={i === 0}
                    />
                    <select
                      value={v.role}
                      aria-label={`Role for teammate ${i + 1}`}
                      onChange={(e) => updateInvite(i, { role: e.target.value as Role })}
                      className="h-10 rounded-md border border-[#C0C0C0]/60 bg-card px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <Button
                      type="button" variant="ghost" size="sm"
                      disabled={invites.length === 1}
                      onClick={() => removeInvite(i)}
                      className="h-10 px-2 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addInvite} className="w-full">
                  <Plus className="mr-2 h-4 w-4" /> Add another teammate
                </Button>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(2)}>Back</Button>
                <Button type="submit" className="flex-1" disabled={busy}>
                  {busy ? "Sending…" : "Send invites & continue"}
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                disabled={busy}
                onClick={async () => { await persistStep(4); setStep(4); }}
              >
                Skip for now — invite the team later
              </Button>
            </form>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <Header
                icon={Sparkles}
                title="Turn on modules"
                subtitle="Enable the field tools your team will use. All optional — toggle any time from Settings."
              />
              <div className="space-y-2">
                <FlagRow
                  icon={Navigation}
                  title="GPS tracking"
                  desc="Live officer locations on the patrol map and shift timelines."
                  checked={flags.gps_tracking}
                  onChange={(v) => setFlags(f => ({ ...f, gps_tracking: v }))}
                />
                <FlagRow
                  icon={Siren}
                  title="SOS panic button"
                  desc="One-tap distress alert that pings supervisors and dispatch."
                  checked={flags.sos_enabled}
                  onChange={(v) => setFlags(f => ({ ...f, sos_enabled: v }))}
                />
                <FlagRow
                  icon={Clock}
                  title="Time clock"
                  desc="On-site clock in/out with geofence verification."
                  checked={flags.time_clock_enabled}
                  onChange={(v) => setFlags(f => ({ ...f, time_clock_enabled: v }))}
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(3)}>Back</Button>
                <Button type="button" className="flex-1" disabled={busy} onClick={saveModulesAndContinue}>
                  {busy ? "Saving…" : "Continue"}
                </Button>
              </div>
            </div>
          )}

          {step === 5 && !soloDone && (
            <div className="space-y-5">
              <Header
                icon={CreditCard}
                title={isSoloFree ? "Activate Free Forever" : soloPaid ? "Solo Contractor plan" : "Pick a plan"}
                subtitle={soloPaid
                  ? "Start your 30-day trial of Solo Contractor, billed monthly or yearly. Or stay on Free Forever."
                  : isSolo
                  ? "No credit card. No trial countdown. Free for solo officers — forever. Upgrade only if you grow into a team."
                  : "Start a 30-day trial on any paid plan, or stay on Free Forever. Change any time in Billing."}
              />

              {!isSoloFree && (
                <div className="flex justify-center">
                  <div className="inline-flex rounded-lg border border-border bg-background/40 p-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setBillingInterval("month")}
                      className={`rounded-md px-3 py-1.5 font-medium transition-colors ${billingInterval === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >Monthly</button>
                    <button
                      type="button"
                      onClick={() => setBillingInterval("year")}
                      className={`rounded-md px-3 py-1.5 font-medium transition-colors ${billingInterval === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >Annual <span className="ml-1 text-[10px] opacity-80">save ~15%</span></button>
                  </div>
                </div>
              )}

              <div className={`grid gap-3 ${isSoloFree ? "" : "sm:grid-cols-2"}`}>
                {plans
                  .filter((p) => isSoloFree ? p.key === "free"
                    : soloPaid ? (p.key === "free" || ((p.key === "solo" || p.key === "solo_annual") && p.interval === billingInterval))
                    : (p.key === "free" || p.interval === billingInterval))
                  .map((p) => {
                    const isFree = p.key === "free";
                    const isChosen = chosenPlanKey === p.key;
                    return (
                      <div
                        key={p.id}
                        className={`flex flex-col rounded-xl border bg-background/40 p-4 transition-colors ${isChosen ? "border-primary shadow-elegant" : "border-border hover:border-primary/40"}`}
                      >
                        <div className="flex items-baseline justify-between">
                          <div className="font-display text-sm font-semibold">{p.name}</div>
                          <div className="text-right">
                            <div className="font-display text-lg font-bold">
                              {isFree ? "Free" : `$${(p.price_cents / 100).toFixed(0)}`}
                            </div>
                            {!isFree && (
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                / {p.interval}
                              </div>
                            )}
                          </div>
                        </div>
                        {p.description && (
                          <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
                        )}
                        {Array.isArray(p.features) && p.features.length > 0 && (
                          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                            {p.features.slice(0, 4).map((f, i) => (
                              <li key={i} className="flex items-start gap-1.5">
                                <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {isFree && (
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            No card required · No 30-day expiry · $0 forever
                          </p>
                        )}
                        <div className="mt-4">
                          <Button
                            type="button"
                            size="sm"
                            variant={isFree ? (isSoloFree ? "default" : "outline") : "default"}
                            className="w-full"
                            disabled={busy}
                            onClick={() => (isFree ? pickFree() : pickPaid(p))}
                          >
                            {isFree
                              ? (isSoloFree ? (busy ? "Activating…" : "Activate Free Forever") : "Stay on Free")
                              : "Start 30-day trial"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {!isSolo && (
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setStep(4)}>
                    Back
                  </Button>
                  <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => finish()}>
                    Skip — decide later
                  </Button>
                </div>
              )}
            </div>
          )}

          {soloDone && (
            <SoloDoneScreen
              orgId={orgId}
              planKey={(() => { try { return localStorage.getItem("mr_plan_preselect"); } catch { return null; } })()}
              onInvite={() => navigate({ to: "/team" })}
              onSkip={() => navigate({ to: "/home", replace: true })}
            />
          )}


        </div>

        <Dialog open={checkoutOpen} onOpenChange={(o) => { if (!o) closeCheckout(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>Start your 30-day free trial</DialogTitle>
            </DialogHeader>
            <div className="p-4">{checkoutElement}</div>
          </DialogContent>
        </Dialog>


        <p className="mt-4 text-center text-xs text-muted-foreground">
          Need to step away? Your progress is saved — you can pick up right where you left off.
        </p>
      </div>
    </div>
  );
}

function Header({ icon: Icon, title, subtitle }: { icon: any; title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        {/* The step title is this screen's main heading, so it is the page h1. */}
        <h1 className="font-display text-xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

function SoloDoneScreen({
  orgId, planKey, onInvite, onSkip,
}: { orgId: string | null; planKey: string | null; onInvite: () => void; onSkip: () => void }) {
  const isFree = planKey === "free";
  const planSlug = isFree ? "free" : "solo";
  const [maxSeats, setMaxSeats] = useState<number | null>(null);
  const [members, setMembers] = useState<number>(1);
  const [invites, setInvites] = useState<Array<{
    id: string; email: string; role: string; token: string;
    accepted_at: string | null; expires_at: string | null;
  }>>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const resendInviteFn = useServerFn(resendInvite);
  const revokeInviteFn = useServerFn(revokeInvite);

  const load = async () => {
    if (!orgId) return;
    const [{ count: memberCount }, { data: inviteRows }] = await Promise.all([
      supabase.from("organization_members")
        .select("user_id", { count: "exact", head: true })
        .eq("organization_id", orgId),
      supabase.from("organization_invites")
        .select("id,email,role,token,accepted_at,expires_at")
        .eq("organization_id", orgId)
        .is("accepted_at", null)
        .order("created_at", { ascending: false }),
    ]);
    setMembers(memberCount ?? 1);
    setInvites((inviteRows ?? []) as typeof invites);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: plan } = await supabase
        .from("subscription_plans")
        .select("max_seats")
        .eq("key", planSlug)
        .maybeSingle();
      if (!cancelled) setMaxSeats(((plan as { max_seats?: number } | null)?.max_seats ?? null));
      if (!cancelled) await load();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, planSlug]);

  const pending = invites.length;
  const usedSeats = members + pending;
  const remaining = maxSeats != null ? Math.max(0, maxSeats - usedSeats) : null;
  const ready = maxSeats != null;
  const allFilled = remaining === 0;

  const copyLink = async (token: string) => {
    if (typeof window === "undefined") return;
    const link = `${window.location.origin}/accept-invite/${token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    catch { toast.error("Couldn't copy. Link: " + link); }
  };

  const handleResend = async (inv: { id: string; token: string }) => {
    setBusyId(inv.id);
    try {
      const updated: any = await resendInviteFn({
        data: { inviteId: inv.id, origin: typeof window !== "undefined" ? window.location.origin : undefined },
      });
      await copyLink(updated?.token ?? inv.token);
      if (updated?.emailSent) toast.success(`Invite re-sent to ${updated.email} — link also copied`);
      else toast.warning(`Invite extended, but the email couldn't be sent${updated?.emailError ? ` (${updated.emailError})` : ""}. Link copied — share it directly.`);

      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't resend invite");
    } finally { setBusyId(null); }
  };

  const handleRevoke = async (id: string) => {
    if (typeof window !== "undefined" && !window.confirm("Revoke this invitation?")) return;
    setBusyId(id);
    try {
      await revokeInviteFn({ data: { inviteId: id } });
      toast.success("Invite revoked — seat freed");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't revoke invite");
    } finally { setBusyId(null); }
  };

  const seatCopy = isFree
    ? "Your Free Forever plan includes seats you can invite to your workspace — bring on a partner, a friend, or your whole crew whenever you're ready."
    : "Your Solo Contractor plan includes a backup seat — invite the officer who covers you when you're off duty so they can clock in, run tours, and respond to alerts.";
  const ctaLabel = isFree ? "Invite teammates now" : "Invite my backup officer";

  return (
    <div className="space-y-5">
      <Header
        icon={Check}
        title="You're all set"
        subtitle="Your workspace is live and your plan is active. One last thing — totally optional."
      />
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Users className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold">
                {isFree ? "Fill your free seats" : "Add your backup officer"}
              </div>
              {ready && (
                <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
                  {usedSeats}/{maxSeats} seats used
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{seatCopy}</p>
            {ready && (
              <p className="mt-2 text-xs font-medium text-foreground">
                {allFilled
                  ? "All seats are filled — upgrade your plan to add more teammates."
                  : `${remaining} invite${remaining === 1 ? "" : "s"} remaining on your ${isFree ? "Free Forever" : "Solo Contractor"} plan.`}
              </p>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">
              You can always manage members and invites later from <span className="font-medium text-foreground">Team → Invite</span>.
            </p>
          </div>
        </div>
      </div>

      {invites.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Pending invitations ({invites.length})</h3>
            <span className="text-[11px] text-muted-foreground">Resend or revoke any time</span>
          </div>
          <ul className="divide-y divide-border">
            {invites.map((inv) => {
              const expires = inv.expires_at ? new Date(inv.expires_at) : null;
              const expired = expires ? expires.getTime() < Date.now() : false;
              const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / 86_400_000) : null;
              return (
                <li key={inv.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{inv.email}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded-full border border-border bg-secondary px-2 py-0.5">{inv.role}</span>
                      {expired ? (
                        <span className="text-destructive">Expired</span>
                      ) : (
                        <span>
                          {daysLeft != null && daysLeft > 0 ? `${daysLeft}d left` : "expires today"}
                          {expires && <> · {expires.toLocaleDateString()}</>}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button type="button" size="sm" variant="ghost"
                            disabled={busyId === inv.id}
                            onClick={() => handleResend(inv)}>
                      {expired ? "Renew" : "Resend"}
                    </Button>
                    <Button type="button" size="sm" variant="ghost"
                            className="text-destructive"
                            disabled={busyId === inv.id}
                            onClick={() => handleRevoke(inv.id)}>
                      Revoke
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" variant="outline" className="flex-1" onClick={onSkip}>
          Maybe later — take me to my dashboard
        </Button>
        <Button type="button" className="flex-1" onClick={onInvite} disabled={allFilled}>
          {allFilled ? "Seats full — upgrade to invite more" : ctaLabel}
        </Button>
      </div>
    </div>
  );
}



function FlagRow({
  icon: Icon, title, desc, checked, onChange,
}: { icon: any; title: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-border bg-background/40 p-4 cursor-pointer hover:border-primary/40 transition-colors">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
